import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { endpoints } from '@/api/endpoints';
import { useApi } from '@/api/useApi';
import { DataTable, type Column } from '@/components/DataTable';
import { StatusBadge, statusLabel } from '@/components/StatusBadge';
import { ListFilterBar } from '@/components/ListFilterBar';
import { useListFilters } from '@/lib/useListFilters';
import { filterOptions } from '@/lib/filterOptions';
import { ErrorBanner } from '@/components/ErrorBanner';
import { OnboardingNotice, NewRequestAction } from '@/components/OnboardingNotice';
import { useLanguage } from '@/i18n/LanguageContext';
import { formatDateTime, formatNumber } from '@/i18n/format';
import { useDraftActions } from '@/lib/drafts';
import { describeRequestError } from '@/lib/requests';
import type { InboundRequestRow } from '@/api/types';

const LIMIT = 50;
const STATUSES = ['DRAFT', 'EXPECTED', 'ARRIVED', 'RECEIVING', 'RECEIVED', 'CLOSED', 'CANCELLED'] as const;

/**
 * "Приёмка": the seller's supplies to the warehouse - requested here or announced by the warehouse. Drafts show
 * edit / send / delete; a sent request has no delete (it is kept in the history; withdrawing lives on its page).
 */
export function InboundListPage(): JSX.Element {
  const [offset, setOffset] = useState(0);
  const list = useListFilters(() => setOffset(0));
  const result = useApi(() => endpoints.inboundRequests({ ...list.query, limit: LIMIT, offset }), [list.key, offset]);
  const options = useApi(() => endpoints.requestOptions(), []);
  const { t, locale } = useLanguage();
  const opts = filterOptions(options.status === 'success' ? options.data.owners : []);
  const reload = result.reload;
  const onChanged = useCallback(() => reload(), [reload]);
  const actions = useDraftActions('INBOUND', onChanged);
  const columns: readonly Column<InboundRequestRow>[] = [
    { key: 'number', header: t('requests.in.colNumber'), render: (r) => <>{r.number}{(r.revision_no ?? 1) > 1 && <span className="revision-mark" title={t('revise.editedBadge')}>{t('revise.editedMark')}</span>}</> },
    ...(opts.owners.length > 1 ? [{ key: 'client', header: t('filters.client'), render: (r: { owner_name?: string }) => r.owner_name ?? '—' }] : []),
    { key: 'state', header: t('common.status'), render: (r) => <StatusBadge value={r.state} /> },
    { key: 'warehouse', header: t('requests.warehouse'), render: (r) => r.warehouse_name },
    { key: 'expected', header: t('requests.in.colExpected'), align: 'right', render: (r) => formatNumber(r.qty_expected, locale) },
    { key: 'received', header: t('requests.in.colReceived'), align: 'right', render: (r) => formatNumber(r.qty_received, locale) },
    { key: 'created', header: t('requests.in.colCreated'), render: (r) => formatDateTime(r.created_at, locale) },
    { key: 'source', header: t('requests.in.colSource'), render: (r) => (r.requested_in_portal ? t('requests.in.sourcePortal') : t('requests.in.sourceWarehouse')) },
    {
      key: 'actions',
      header: t('drafts.actions'),
      render: (r) =>
        r.state === 'DRAFT' && r.requested_in_portal ? (
          <span className="row-actions" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
            <Link className="button-link" to={`/inbound/${r.id}/edit`} aria-label={`${t('drafts.edit')} ${r.number}`}>{t('drafts.edit')}</Link>
            <button type="button" className="primary" disabled={actions.busyId === r.id} onClick={() => void actions.send(r.id)} aria-label={`${t('drafts.send')} ${r.number}`}>
              {actions.busyId === r.id ? t('drafts.sending') : t('drafts.send')}
            </button>
            <button type="button" className="danger" disabled={actions.busyId === r.id} onClick={() => void actions.remove(r.id)} aria-label={`${t('drafts.delete')} ${r.number}`}>
              {t('drafts.delete')}
            </button>
          </span>
        ) : null,
    },
  ];
  return (
    <div>
      <div className="page-header">
        <h1>{t('nav.inbound')}</h1>
        <NewRequestAction to="/inbound/new" label={t('requests.in.new')} />
      </div>
      <OnboardingNotice />
      {actions.error !== null && (
        describeRequestError(t, actions.error)
          ? <div className="error-banner" role="alert">{describeRequestError(t, actions.error)}</div>
          : <ErrorBanner error={actions.error} />
      )}
      <ListFilterBar
        filters={list.filters}
        onChange={list.set}
        onReset={list.reset}
        filtered={list.filtered}
        statuses={STATUSES.map((s) => ({ value: s, label: statusLabel(t, s) }))}
        owners={opts.owners}
        warehouses={opts.warehouses}
      />
      <DataTable
        result={result}
        columns={columns}
        getRowHref={(r) => `/inbound/${r.id}`}
        limit={LIMIT}
        offset={offset}
        onPageChange={setOffset}
        emptyTitle={list.filtered ? t('filters.noMatch') : t('requests.in.emptyTitle')}
        emptyHint={list.filtered ? undefined : t('requests.in.emptyHint')}
      />
    </div>
  );
}
